import { useCallback, useEffect, useState } from "react";
import { getSessionData } from "@repo/shared-utils/common-utils";
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/global-store";
import { Reminder } from "../../types/upcoming-live.types";
import { ReminderService } from "./UpcomingLive";

export function useReminders(reminderService?: ReminderService, influencerId?: string) {
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const [reminders, setReminders] = useState<Map<string, Reminder>>(new Map());

  const fetchAllReminders = useCallback(async () => {
    if (!reminderService) {
      return;
    }

    try {
      const sessionData = await getSessionData();
      const mobileNo = sessionData?.mobileNo;

      if (!mobileNo) {
        return;
      }

      const response = await reminderService.getUserReminders({ mobileNo, influencerId });

      if (response?.data?.reminders) {
        const remindersMap = new Map<string, Reminder>();
        response.data.reminders.forEach((reminder) => {
          if (reminder.isActive && reminder.videoId) {
            remindersMap.set(reminder.videoId, reminder);
          }
        });
        setReminders(remindersMap);
      }
    } catch {
      // Reminder fetch failures leave the existing map unchanged
    }
  }, [influencerId, reminderService]);

  useEffect(() => {
    if (isLoggedIn && reminderService) {
      fetchAllReminders();
    }
  }, [isLoggedIn, fetchAllReminders, reminderService]);

  const handleReminderToggle = useCallback(
    async (videoId: string, isSet: boolean) => {
      if (!isSet) {
        setReminders((prev) => {
          const newMap = new Map(prev);
          newMap.delete(videoId);
          return newMap;
        });
      }

      if (isLoggedIn && reminderService) {
        await fetchAllReminders();
      }
    },
    [isLoggedIn, fetchAllReminders, reminderService]
  );

  return { reminders, handleReminderToggle };
}
